import { XIcon } from 'lucide-react';
import type { JSX } from 'react';

import { CtLogo } from '@/components/brand/CtLogo.js';
import { Button } from '@/components/ui/button.js';

interface UpdateAvailableToastProps {
  /** True while the refresh is in flight — the button locks so the state is visible until navigation. */
  refreshing?: boolean;
  onRefresh: () => void;
  onDismiss: () => void;
}

/**
 * "Update available" notification. Rendered through a plain React portal
 * (see useVersionCheck) — sonner's portal layering was unreliable when
 * a Radix Dialog overlay was open. Self-contained card chrome so the
 * banner reads as a popover regardless of where it's mounted.
 */
export function UpdateAvailableToast({
  refreshing = false,
  onRefresh,
  onDismiss,
}: UpdateAvailableToastProps): JSX.Element {
  return (
    <div className="relative w-[32rem] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-elev-5">
      <div className="flex w-full items-center gap-3 pr-6">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/12 text-primary">
          <CtLogo variant="mark" tone="mono" className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight">
            A new version of CT Agent is ready
          </p>
          <p className="mt-1 truncate text-xs leading-relaxed text-muted-foreground">
            Refresh to load the latest update.
          </p>
        </div>
        <Button
          variant="brand"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss update notification"
        className="absolute right-1.5 top-1.5 inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  );
}
